export default function initializeBoxAnimatedNumber() {
    document.querySelectorAll('.box-animated-number__number').forEach(function (numberElement) {
        let animationStarted = false;
        const originalText = numberElement.dataset.number || numberElement.textContent.trim();

        if (originalText) {
            // Parse the number string to extract numeric parts and formatting
            const parsed = parseNumberString(originalText);

            if (parsed.numbers.length > 0) {
                // Initialize with zeros
                numberElement.innerHTML = formatNumberString(parsed, parsed.numbers.map(() => 0));

                // Check if element is in viewport on load
                if (isElementInViewport(numberElement)) {
                    animationStarted = startAnimation(numberElement, parsed, animationStarted);
                }

                // Listen for scroll
                window.addEventListener('scroll', () => {
                    if (isElementInViewport(numberElement)) {
                        animationStarted = startAnimation(numberElement, parsed, animationStarted);
                    }
                }, { passive: true });
            }
        }
    });
}

function parseNumberString(text) {
    // Find all number sequences (including decimals and commas as thousands separators)
    // Pattern: digits, optionally followed by comma/dot and more digits
    const numberRegex = /(\d+(?:[.,]\d+)*)/g;
    const numbers = [];
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = numberRegex.exec(text)) !== null) {
        // Add text before number
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                value: text.substring(lastIndex, match.index)
            });
        }

        // Parse number - handle commas as thousands separators
        const numberStr = match[0];
        let numberValue;

        // Simple heuristic: if comma is followed by exactly 3 digits and no more separators, it's thousands
        if (numberStr.includes(',')) {
            const commaParts = numberStr.split(',');
            // Check if all parts after first comma have 3 digits (thousands separator pattern)
            const isThousands = commaParts.slice(1).every(part => part.length === 3 && /^\d{3}$/.test(part));

            if (isThousands && commaParts.length > 1) {
                // Thousands separator - remove commas
                numberValue = parseFloat(numberStr.replace(/,/g, ''));
            } else {
                // Decimal separator - replace comma with dot
                numberValue = parseFloat(numberStr.replace(',', '.'));
            }
        } else if (numberStr.includes('.')) {
            // Check if dot is thousands separator (3 digits after)
            const dotParts = numberStr.split('.');
            const isThousands = dotParts.slice(1).every(part => part.length === 3 && /^\d{3}$/.test(part));

            if (isThousands && dotParts.length > 1) {
                // Thousands separator (European format) - remove dots
                numberValue = parseFloat(numberStr.replace(/\./g, ''));
            } else {
                // Decimal separator
                numberValue = parseFloat(numberStr);
            }
        } else {
            numberValue = parseFloat(numberStr);
        }

        numbers.push(numberValue);
        parts.push({
            type: 'number',
            value: numberStr, // Keep original format for display
            numericValue: numberValue
        });

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push({
            type: 'text',
            value: text.substring(lastIndex)
        });
    }

    return {
        parts: parts,
        numbers: numbers
    };
}

function formatNumberString(parsed, animatedNumbers) {
    let result = '';
    let numberIndex = 0;

    for (const part of parsed.parts) {
        if (part.type === 'text') {
            result += part.value;
        } else {
            // Format the animated number with original formatting
            const originalFormat = part.value;
            const animatedValue = animatedNumbers[numberIndex];

            // Check if original had commas as thousands separator
            if (originalFormat.includes(',')) {
                const commaParts = originalFormat.split(',');
                // Check if all parts after first comma have 3 digits (thousands separator pattern)
                const isThousands = commaParts.slice(1).every(part => part.length === 3 && /^\d{3}$/.test(part));

                if (isThousands && commaParts.length > 1) {
                    // Thousands separator - format with commas
                    result += formatNumberWithCommas(animatedValue);
                } else {
                    // Decimal separator - preserve decimal places
                    const decimalPlaces = commaParts[1]?.length || 0;
                    result += animatedValue.toFixed(decimalPlaces).replace('.', ',');
                }
            } else if (originalFormat.includes('.')) {
                const dotParts = originalFormat.split('.');
                // Check if all parts after first dot have 3 digits (thousands separator pattern)
                const isThousands = dotParts.slice(1).every(part => part.length === 3 && /^\d{3}$/.test(part));

                if (isThousands && dotParts.length > 1) {
                    // Thousands separator (European format) - format with dots
                    result += formatNumberWithDots(animatedValue);
                } else {
                    // Decimal separator
                    const decimalPlaces = dotParts[1]?.length || 0;
                    result += animatedValue.toFixed(decimalPlaces);
                }
            } else {
                // Integer
                result += Math.round(animatedValue).toString();
            }

            numberIndex++;
        }
    }

    return result;
}

function formatNumberWithDots(num) {
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function formatNumberWithCommas(num) {
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
        rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
        rect.bottom > 0 &&
        rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
        rect.right > 0
    );
}

function startAnimation(element, parsed, animationStarted) {
    if (animationStarted === false && parsed.numbers.length > 0) {
        animationStarted = true;
        animateNumbers(element, parsed, parsed.numbers.map(() => 0), parsed.numbers, 2000);
    }
    return animationStarted;
}

function animateNumbers(element, parsed, startValues, endValues, duration) {
    const startTime = performance.now();
    const ranges = endValues.map((end, i) => end - startValues[i]);

    function animate(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out)
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        // Calculate current values
        const currentValues = startValues.map((start, i) =>
            start + (ranges[i] * easedProgress)
        );

        // Update display
        element.innerHTML = formatNumberString(parsed, currentValues);

        if (progress < 1) {
            requestAnimationFrame(animate);
        } else {
            // Ensure final values are exact
            element.innerHTML = formatNumberString(parsed, endValues);
        }
    }

    requestAnimationFrame(animate);
}
